import { updateEmail, updatePassword } from 'firebase/auth';
// Firestore updates are handled via updateUserProfile from AuthContext
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteProfileModal from '../components/DeleteProfileModal';
import MentionInput from '../components/MentionInput';
import { storage } from '../firebase';
import { generateCoverVariants } from '../utils/coverImageUtils';
import { useAuth } from '../hooks/useAuth';
import '../styles/EditProfile.css';
import { syncProfilePictureEverywhere } from '../utils/profilePictureSync';
import { isUsernameAvailable, validateUsername } from '../utils/usernameUtils';

function EditProfile() {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    username: '',
    bio: '',
    profilePicture: '',
    isPro: false,
    shopEnabled: false,
    coverImage: ''
  });
  
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  const [newEmail, setNewEmail] = useState('');
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [usernameValidation, setUsernameValidation] = useState({ valid: true, error: '', checking: false });
  const [originalUsername, setOriginalUsername] = useState('');

  useEffect(() => {
    if (userProfile) {
      setFormData({
        username: userProfile.username || '',
        bio: userProfile.bio || '',
        profilePicture: userProfile.profilePicture || '',
        isPro: !!userProfile.isPro,
        shopEnabled: !!userProfile.shopEnabled,
        coverImage: userProfile.coverImage || ''
      });
      setOriginalUsername(userProfile.username || '');
    }
    if (currentUser) {
      setNewEmail(currentUser.email || '');
    }
  }, [userProfile, currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validation spéciale pour username
    if (name === 'username') {
      handleUsernameChange(value);
    }
  };

  const handleUsernameChange = async (username) => {
    setUsernameValidation({ valid: true, error: '', checking: true });
    
    // Validation du format
    const formatValidation = validateUsername(username);
    if (!formatValidation.valid) {
      setUsernameValidation({ valid: false, error: formatValidation.error, checking: false });
      return;
    }
    
    // Si c'est le même username qu'avant, pas besoin de vérifier la disponibilité
    if (username === originalUsername) {
      setUsernameValidation({ valid: true, error: '', checking: false });
      return;
    }
    
    // Vérifier la disponibilité
    try {
      const isAvailable = await isUsernameAvailable(username);
      if (!isAvailable) {
        setUsernameValidation({ 
          valid: false, 
          error: 'Ce nom d\'utilisateur est déjà pris', 
          checking: false 
        });
      } else {
        setUsernameValidation({ valid: true, error: '', checking: false });
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du username:', error);
      setUsernameValidation({ 
        valid: false, 
        error: 'Erreur lors de la vérification', 
        checking: false 
      });
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image valide');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      setProfilePictureFile(file);
      setError('');
    }
  };

  const handleCoverFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner une image de couverture valide');
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        setError('La couverture ne doit pas dépasser 8MB');
        return;
      }
      setCoverImageFile(file);
      setError('');
    }
  };

  const uploadProfilePicture = async () => {
    if (!profilePictureFile) {
      // If no new file, return existing profile picture URL (from userProfile, not formData)
      const existingURL = userProfile?.profilePicture || userProfile?.photoURL || '';
      return existingURL;
    }

    const fileExtension = profilePictureFile.name.split('.').pop();
    const fileName = `profile_${currentUser.uid}_${Date.now()}.${fileExtension}`;
    const storageRef = ref(storage, `profile-pictures/${fileName}`);
    
    try {
      const snapshot = await uploadBytes(storageRef, profilePictureFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('❌ Error uploading profile picture:', error);
      throw error;
    }
  };

  const uploadCoverImage = async () => {
    if (!coverImageFile) {
      return { url: userProfile?.coverImage || '', set: userProfile?.coverImageSet || null };
    }
    try {
      // Generate responsive variants (3:1 center-crop)
      const variants = await generateCoverVariants(coverImageFile);

      // Upload each variant with deterministic path
      const ts = Date.now();
      const basePath = `cover-images/${currentUser.uid}/${ts}`;

      const [smSnap, mdSnap, lgSnap] = await Promise.all([
        uploadBytes(ref(storage, `${basePath}_sm.jpg`), variants.sm),
        uploadBytes(ref(storage, `${basePath}_md.jpg`), variants.md),
        uploadBytes(ref(storage, `${basePath}_lg.jpg`), variants.lg),
      ]);

      const [smURL, mdURL, lgURL] = await Promise.all([
        getDownloadURL(smSnap.ref),
        getDownloadURL(mdSnap.ref),
        getDownloadURL(lgSnap.ref),
      ]);

      return { url: lgURL, set: { sm: smURL, md: mdURL, lg: lgURL } };
    } catch (error) {
      console.error('❌ Error uploading cover image and variants:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Vérifier que le username est présent et valide
      if (!formData.username.trim()) {
        setError('Le nom d\'utilisateur est requis.');
        setLoading(false);
        return;
      }
      
      if (!usernameValidation.valid) {
        setError('Veuillez corriger le nom d\'utilisateur avant de sauvegarder.');
        setLoading(false);
        return;
      }
      
      
      // Upload images if changed
      const [profilePictureURL, coverResult] = await Promise.all([
        uploadProfilePicture(),
        uploadCoverImage()
      ]);
      
      const isPro = !!formData.isPro;
      const shopEnabled = isPro && !!formData.shopEnabled;

      const updatedProfile = {
        username: formData.username,
        bio: formData.bio,
        isPro,
        shopEnabled,
        updatedAt: new Date()
      };
      
      // Only add profilePicture if we have a valid URL
      if (profilePictureURL && profilePictureURL.trim() !== '') {
        updatedProfile.profilePicture = profilePictureURL;
      }
      if (coverResult && coverResult.url) {
        updatedProfile.coverImage = coverResult.url;
        if (coverResult.set) {
          updatedProfile.coverImageSet = coverResult.set;
        }
      }
      // Persist via AuthContext helper (updates Firestore and local context)
      await updateUserProfile(updatedProfile);
      
      // If profile picture changed, sync across posts/comments
      if (profilePictureURL && profilePictureURL.trim() !== '' && profilePictureFile) {
        try {
          await syncProfilePictureEverywhere(currentUser.uid, profilePictureURL);
        } catch (syncError) {
          console.error('❌ Error syncing profile picture:', syncError);
          setError('Photo de profil mise à jour, mais erreur lors de la synchronisation avec les contenus existants');
        }
      }
      
      // Update email if changed
      if (newEmail !== currentUser.email) {
        await updateEmail(currentUser, newEmail);
      }
      
      // Update password if provided
      if (passwords.newPassword) {
        if (passwords.newPassword !== passwords.confirmPassword) {
          throw new Error('Les mots de passe ne correspondent pas');
        }
        if (passwords.newPassword.length < 6) {
          throw new Error('Le mot de passe doit contenir au moins 6 caractères');
        }
        await updatePassword(currentUser, passwords.newPassword);
        setPasswords({ newPassword: '', confirmPassword: '' });
      }
      
      setMessage('Profil mis à jour avec succès');
      
      // Redirect to profile after successful update and force refresh
      setTimeout(() => {
        const profileUrl = `/profile/${userProfile?.username || currentUser.uid}?updated=true&t=${Date.now()}`;
        navigate(profileUrl);
        // Force page reload to ensure fresh data
        setTimeout(() => window.location.reload(), 100);
      }, 1500);
      
    } catch (error) {
      console.error('EditProfile - Error updating profile:', error);
      setError(`Erreur lors de la mise à jour: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/profile/${userProfile?.username || currentUser.uid}`);
  };

  if (!currentUser) {
    return (
      <div className="edit-profile-container">
        <p>Vous devez être connecté pour modifier votre profil.</p>
      </div>
    );
  }

  return (
    <div className="edit-profile-container">
      <div className="edit-profile-card">
        <h2>Modifier le profil</h2>
        
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}
        
        <form onSubmit={handleSubmit} className="edit-profile-form">
          <div className="form-group">
            <label htmlFor="username">
              Nom d'utilisateur
              <span className="username-help">
                (Uniquement lettres, chiffres et underscores - pas d'espaces)
              </span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="votre_nom_utilisateur"
              required
              pattern="[a-zA-Z0-9_]{3,30}"
              title="Nom d'utilisateur : 3-30 caractères, lettres, chiffres et underscores uniquement"
            />
            {usernameValidation.checking && (
              <div className="username-status checking">Vérification...</div>
            )}
            {!usernameValidation.checking && !usernameValidation.valid && (
              <div className="username-status error">{usernameValidation.error}</div>
            )}
            {!usernameValidation.checking && usernameValidation.valid && formData.username && (
              <div className="username-status success">✓ Nom d'utilisateur disponible</div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="bio">Bio</label>
            <MentionInput
              value={formData.bio}
              onChange={(value) => setFormData(prev => ({ ...prev, bio: value }))}
              placeholder="Parlez-nous de vous..."
              className="bio-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="profilePicture">Photo de profil</label>
            {formData.profilePicture && (
              <div className="current-picture">
                <img 
                  src={formData.profilePicture} 
                  alt="Photo de profil actuelle" 
                  className="profile-preview"
                />
                <p>Photo actuelle</p>
              </div>
            )}
            <input
              type="file"
              id="profilePicture"
              accept="image/*"
              onChange={handleFileChange}
            />
            {profilePictureFile && (
              <p className="file-selected">
                Nouveau fichier sélectionné: {profilePictureFile.name}
              </p>
            )}
          </div>

          <div className="pro-section">
            <h3>Profil Pro (MVP)</h3>
            <div className="form-row">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={!!formData.isPro}
                  onChange={(e) => setFormData((p) => ({ ...p, isPro: e.target.checked, shopEnabled: e.target.checked ? p.shopEnabled : false }))}
                />
                Activer le profil Pro
              </label>
            </div>
            <div className="form-row">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={!!formData.shopEnabled}
                  disabled={!formData.isPro}
                  onChange={(e) => setFormData((p) => ({ ...p, shopEnabled: e.target.checked }))}
                />
                Activer la boutique
              </label>
              {!formData.isPro && (
                <div className="help-text">Disponible uniquement avec un profil Pro</div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="coverImage">Image de couverture</label>
              {formData.coverImage && (
                <div className="current-picture">
                  <img src={formData.coverImage} alt="Couverture actuelle" className="cover-preview" />
                  <p>Couverture actuelle</p>
                </div>
              )}
              <input type="file" id="coverImage" accept="image/*" onChange={handleCoverFileChange} />
              {coverImageFile && <p className="file-selected">Nouveau fichier: {coverImageFile.name}</p>}
              <p className="help-text">Ratio recommandé 3:1, max 8MB.</p>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="votre@email.com"
              required
            />
          </div>
          
          <div className="password-section">
            <h3>Changer le mot de passe (optionnel)</h3>
            <div className="form-group">
              <label htmlFor="newPassword">Nouveau mot de passe</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwords.newPassword}
                onChange={handlePasswordChange}
                placeholder="Nouveau mot de passe"
                minLength="6"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={handlePasswordChange}
                placeholder="Confirmer le nouveau mot de passe"
                minLength="6"
              />
            </div>
          </div>
          
          {/* Section de suppression de profil */}
          <div className="danger-zone" style={{ 
            background: '#fff5f5', 
            border: '2px solid #red', 
            borderRadius: '8px', 
            padding: '20px', 
            marginTop: '30px', 
            textAlign: 'center',
            display: 'block !important',
            visibility: 'visible !important'
          }}>
            <h3 style={{ color: '#dc3545', fontSize: '1.5rem', fontWeight: 'bold' }}>
              ⚠️ Zone de danger ⚠️
            </h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              La suppression de votre profil est irréversible et supprimera tous vos contenus.
            </p>
            <button 
              type="button" 
              onClick={() => {
                setShowDeleteModal(true);
              }}
              className="btn-danger"
              disabled={loading}
              style={{
                background: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '15px 30px',
                borderRadius: '8px',
                fontSize: '1.1rem',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🗑️ Supprimer mon profil MAINTENANT
            </button>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              onClick={handleCancel}
              className="btn-secondary"
              disabled={loading}
            >
              Annuler
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Mise à jour...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Modal de suppression de profil */}
      <DeleteProfileModal 
        isOpen={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)} 
      />
    </div>
  );
}

export default EditProfile;
